// Groups lower-frequency view and table preferences under one toolbar disclosure.
import { Settings2 } from "lucide-react";
import type { ReactNode } from "react";
import type { SavedViewRecord } from "../../lib/saved-views-api";
import type { ScreenerPageSize } from "../../lib/types";
import { Button } from "../ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { SavedViewActions } from "./SavedViewActions";

interface ToolbarCustomizeProps {
  pageSize: ScreenerPageSize;
  onPageSizeChange: (pageSize: ScreenerPageSize) => void;
  selectedSavedView: SavedViewRecord | null;
  authenticated: boolean;
  accountAvailability: "loading" | "ready" | "error";
  savedViewMutationPending: boolean;
  onCreateSavedView: (name: string) => void;
  onRenameSavedView: (view: SavedViewRecord, name: string) => void;
  onDeleteSavedView: (view: SavedViewRecord) => void;
  tableControls?: ReactNode;
}

/** Reveals all optional table and saved-view controls from one clear entry point. */
export function ToolbarCustomize({
  pageSize,
  onPageSizeChange,
  selectedSavedView,
  authenticated,
  accountAvailability,
  savedViewMutationPending,
  onCreateSavedView,
  onRenameSavedView,
  onDeleteSavedView,
  tableControls
}: ToolbarCustomizeProps) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" aria-label="Customize table">
          <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="hidden sm:inline">Customize</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        role="dialog"
        aria-label="Customize table"
        align="end"
        className="w-[min(32rem,calc(100vw-1rem))] space-y-4"
      >
        <div>
          <h3 className="text-sm font-semibold">Customize table</h3>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            Manage this view, visible columns, density, and page size.
          </p>
        </div>

        <section aria-labelledby="view-management-heading" className="space-y-2">
          <h4 id="view-management-heading" className="text-xs font-semibold">
            View management
          </h4>
          <SavedViewActions
            selectedSavedView={selectedSavedView}
            authenticated={authenticated}
            accountAvailability={accountAvailability}
            mutationPending={savedViewMutationPending}
            onCreate={onCreateSavedView}
            onRename={onRenameSavedView}
            onDelete={onDeleteSavedView}
          />
        </section>

        <section aria-labelledby="table-preferences-heading" className="space-y-2 border-t border-border pt-3">
          <h4 id="table-preferences-heading" className="text-xs font-semibold">
            Table preferences
          </h4>
          <div className="flex flex-wrap items-center gap-2">
            {tableControls}
            <label className="flex items-center gap-1.5 text-muted-foreground">
              <span>Rows</span>
              <select
                className="h-8 rounded-md border border-input bg-surface-elevated px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                value={pageSize}
                onChange={(event) =>
                  onPageSizeChange(Number(event.target.value) as ScreenerPageSize)
                }
              >
                <option value={50}>50</option>
                <option value={100}>100</option>
                <option value={250}>250</option>
              </select>
            </label>
          </div>
        </section>
      </PopoverContent>
    </Popover>
  );
}
